import type { Lien } from "pages/liens/LienTypes";
import type { ClaimsDataType, ServiceDateOfInjury } from "types/DataTypes";

// base interface for ICD fields, exported and used in types/DataTypes.ts and pages/liens/LienTypes.ts
export type ICDFields = {
  icd_indicator: string | null;
  alleged_cause_of_injury: string | null;
} & {
  [K in `icd_diag_code_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19}`]:
    | string
    | null;
} & {
  [K in `icd_diag_code_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19}_verbose`]: string;
} & {
  [K in `icd_diag_code_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19}_accepted`]: boolean;
};

export type ICDCodeEntry = {
  code: string | null;
  verbose: string | null;
  accepted: boolean;
};

export type ICDObject = {
  id?: number;
  alleged_cause_of_injury: string | null;
  icd_indicator: string | null;
  icd_codes: Record<number, ICDCodeEntry>;
};

/** Flat payload for persisting ICD field updates via patch APIs */
export type ICDUpdateValue = string | boolean | null;
export type ICDUpdateData = Record<string, ICDUpdateValue>;

/**
 * adapters for converting different data structures to a common ICD format
 * for use with the ICDCodingBlock component
 */

/**
 * convert a ServiceDateOfInjury to an ICDObject
 * @param serviceDOI the ServiceDateOfInjury object to convert
 * @returns an ICDObject with the relevant ICD coding data
 */
export const serviceDoiToIcdObject = (
  serviceDOI?: ServiceDateOfInjury | null,
): ICDObject => {
  if (!serviceDOI) {
    return {
      alleged_cause_of_injury: null,
      icd_indicator: null,
      icd_codes: {},
    };
  }

  // extract all ICD codes from the ServiceDateOfInjury
  const icdCodes: Record<number, ICDCodeEntry> = {};

  // process all possible ICD code fields (1-19)
  for (let i = 1; i <= 19; i++) {
    const codeKey = `icd_diag_code_${i}`;
    const verboseKey = `${codeKey}_verbose`;

    if (serviceDOI[codeKey as keyof ServiceDateOfInjury]) {
      icdCodes[i] = {
        code: serviceDOI[codeKey as keyof ServiceDateOfInjury] as string,
        verbose: serviceDOI[verboseKey as keyof ServiceDateOfInjury] as string,
        accepted:
          (serviceDOI[
            `${codeKey}_accepted` as keyof ServiceDateOfInjury
          ] as boolean) || false,
      };
    }
  }

  return {
    id: serviceDOI.id,
    alleged_cause_of_injury: serviceDOI.alleged_cause_of_injury,
    icd_indicator: serviceDOI.icd_indicator,
    icd_codes: icdCodes,
  };
};

/**
 * convert a Lien to an ICDObject
 * @param lien the Lien object to convert
 * @returns an ICDObject with the relevant ICD coding data
 */
export const lienToIcdObject = (lien?: Lien | null): ICDObject => {
  if (!lien) {
    return {
      alleged_cause_of_injury: null,
      icd_indicator: null,
      icd_codes: {},
    };
  }

  // extract all ICD codes from the Lien
  const icdCodes: Record<number, ICDCodeEntry> = {};

  // process all possible ICD code fields (1-19)
  for (let i = 1; i <= 19; i++) {
    const codeKey = `icd_diag_code_${i}`;
    const verboseKey = `${codeKey}_verbose`;

    if (lien[codeKey as keyof Lien]) {
      icdCodes[i] = {
        code: lien[codeKey as keyof Lien] as string,
        verbose: lien[verboseKey as keyof Lien] as string,
        accepted:
          (lien[`${codeKey}_accepted` as keyof Lien] as boolean) || false,
      };
    }
  }

  return {
    id: lien.id,
    alleged_cause_of_injury: lien.alleged_cause_of_injury,
    icd_indicator: lien.icd_indicator,
    icd_codes: icdCodes,
  };
};

/**
 * convert a ClaimsDataType to an ICDObject
 * @param claim the ClaimsDataType object to convert
 * @returns an ICDObject with the relevant ICD coding data
 */
export const claimToIcdObject = (claim?: ClaimsDataType | null): ICDObject => {
  if (!claim) {
    return {
      alleged_cause_of_injury: null,
      icd_indicator: null,
      icd_codes: {},
    };
  }

  // extract all ICD codes from the Claim
  const icdCodes: Record<number, ICDCodeEntry> = {};

  // process all possible ICD code fields (1-19)
  for (let i = 1; i <= 19; i++) {
    const codeKey = `icd_diag_code_${i}`;
    const verboseKey = `${codeKey}_verbose`;

    if (claim[codeKey as keyof ClaimsDataType]) {
      icdCodes[i] = {
        code: claim[codeKey as keyof ClaimsDataType] as string,
        verbose: claim[verboseKey as keyof ClaimsDataType] as string,
        accepted:
          (claim[`${codeKey}_accepted` as keyof ClaimsDataType] as boolean) ||
          false,
      };
    }
  }

  return {
    id: claim.id,
    alleged_cause_of_injury: claim.alleged_cause_of_injury,
    icd_indicator: claim.icd_indicator,
    icd_codes: icdCodes,
  };
};

/**
 * convert an ICDObject back to a partial object that can be used for updates
 * @param icdObject the ICDObject to convert
 * @returns a flattened object with all ICD-related fields
 */
export const icdObjectToUpdateData = (icdObject: ICDObject): ICDUpdateData => {
  const updateData: ICDUpdateData = {
    alleged_cause_of_injury: icdObject.alleged_cause_of_injury,
    icd_indicator: icdObject.icd_indicator,
  };

  // flatten the icd_codes object back to individual fields
  Object.entries(icdObject.icd_codes).forEach(([index, codeEntry]) => {
    updateData[`icd_diag_code_${index}`] = codeEntry.code;
    updateData[`icd_diag_code_${index}_verbose`] = codeEntry.verbose;
    updateData[`icd_diag_code_${index}_accepted`] = codeEntry.accepted;
  });

  return updateData;
};
